import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertAPI } from '../services/api';
import { vaiA, haDestinazione, ETICHETTA } from '../utils/destinazione';
import '../styles/Alert.css';

/* mostra la data ESATTAMENTE come salvata, senza conversione di fuso */
const fmtDT = (s) => {
    if (!s) return '';
    const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return String(s);
    const [, y, mo, d, h, mi] = m;
    return `${d}/${mo}/${y}, ${h}:${mi}`;
};

/* formatta una Date locale del browser in "YYYY-MM-DDTHH:MM" (ora locale) */
const pad = (n) => String(n).padStart(2, '0');
const toLocalInput = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const AlertNotifier = () => {
    const [due, setDue] = useState([]);
    const [snoozeId, setSnoozeId] = useState(null);   // alert in modalità "posticipa a..."
    const [snoozeVal, setSnoozeVal] = useState('');
    const navigate = useNavigate();

    const check = useCallback(async () => {
        try {
            const r = await alertAPI.getDue();
            if (r.data.success) setDue(r.data.data.alert || []);
        } catch (e) { /* silenzioso */ }
    }, []);

    useEffect(() => {
        check();
        const t = setInterval(check, 60000);
        return () => clearInterval(t);
    }, [check]);

    if (!due.length) return null;

    const rimuovi = (id) => setDue(prev => prev.filter(a => a.id !== id));

    // OK: segna notificato (non ricompare finché non lo riattivi)
    const ok = async (a) => {
        try { await alertAPI.markNotificato(a.id); } catch (e) {}
        rimuovi(a.id);
    };

    /* Il riquadro portava sempre alla scheda generica del paziente, anche
       quando l'avviso diceva ESATTAMENTE dove andare: `riferimento_tipo` e
       `riferimento_id` arrivano gia' da /alert/due, e restavano inusati
       qui mentre dashboard, promemoria e compiti li adoperavano da tempo.
       Chi riceveva «manca il consenso sulla visita del 3 marzo» atterrava
       sull'anagrafica e la visita doveva ritrovarsela.

       `vaiA` ripiega sulla scheda del paziente quando il riferimento non
       c'e' o e' di un tipo che questa versione non conosce: meno preciso,
       mai sbagliato. Per gli avvisi di servizio - un lotto, un resoconto
       del Sistema TS - il paziente non c'e' affatto, e prima il pulsante
       portava a `/pazienti/null`. */
    const vai = async (a) => {
        await ok(a);
        if (haDestinazione(a)) vaiA(navigate, a);
        else if (a.paziente_id) navigate(`/pazienti/${a.paziente_id}`);
    };

    /* Il pulsante dice dove porta: «Vai al paziente» su un avviso che apre
       il magazzino era una promessa sbagliata.
       «Apri» e non «Vai a» perche' le etichette portano gia' l'articolo -
       «il magazzino», «la visita» - e «Vai a il magazzino» non si legge.
       E' anche la formula che usano gia' Promemoria e Compiti. */
    const etichettaVai = (a) => (haDestinazione(a) && ETICHETTA[a.riferimento_tipo]
        ? `Apri ${ETICHETTA[a.riferimento_tipo]}`
        : 'Vai al paziente');

    // Contrassegna come eseguito
    const eseguito = async (a) => {
        try { await alertAPI.update(a.id, { stato: 'fatto', notificato: true }); } catch (e) {}
        rimuovi(a.id);
    };

    // Posticipa di 1 ora rispetto ad ADESSO
    const posticipa1h = async (a) => {
        const nuovo = toLocalInput(new Date(Date.now() + 60 * 60 * 1000));
        try { await alertAPI.update(a.id, { data_ora: nuovo, notificato: false }); } catch (e) {}
        rimuovi(a.id);
    };

    // Posticipa a data/ora scelta
    const apriSnooze = (a) => {
        setSnoozeId(a.id);
        setSnoozeVal(toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
    };
    const confermaSnooze = async (a) => {
        if (!snoozeVal) return;
        try { await alertAPI.update(a.id, { data_ora: snoozeVal, notificato: false }); } catch (e) {}
        setSnoozeId(null); setSnoozeVal('');
        rimuovi(a.id);
    };

    const chiudiTutti = async () => {
        await Promise.all(due.map(a => alertAPI.markNotificato(a.id).catch(() => {})));
        setDue([]);
    };

    return (
        <div className="al-notif-overlay">
            <div className="al-notif">
                <div className="al-notif-head">
                    <span>Promemoria ({due.length})</span>
                    <button className="al-notif-close" onClick={chiudiTutti} title="Chiudi tutti">✕</button>
                </div>
                <div className="al-notif-body">
                    {due.map(a => (
                        <div key={a.id} className="al-notif-item">
                            <div className="al-notif-dt">{fmtDT(a.data_ora)}</div>
                            <div className="al-notif-titolo">{a.titolo}</div>
                            <div className="al-notif-pz">Paziente: {a.paziente_nome} {a.paziente_cognome}</div>
                            {a.descrizione && <div className="al-notif-desc">{a.descrizione}</div>}

                            {snoozeId === a.id ? (
                                <div className="al-notif-snooze">
                                    <input type="datetime-local" value={snoozeVal}
                                           onChange={e => setSnoozeVal(e.target.value)} />
                                    <div className="al-notif-snooze-actions">
                                        <button className="al-notif-go" onClick={() => confermaSnooze(a)}>Conferma</button>
                                        <button className="al-notif-ok" onClick={() => { setSnoozeId(null); setSnoozeVal(''); }}>Annulla</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="al-notif-actions">
                                    {(haDestinazione(a) || a.paziente_id) && (
                                        <button className="al-notif-go" onClick={() => vai(a)}>
                                            {etichettaVai(a)}
                                        </button>
                                    )}
                                    <button className="al-notif-done" onClick={() => eseguito(a)}>Eseguito</button>
                                    <button className="al-notif-snz" onClick={() => posticipa1h(a)}>+1h</button>
                                    <button className="al-notif-snz" onClick={() => apriSnooze(a)}>Posticipa a…</button>
                                    <button className="al-notif-ok" onClick={() => ok(a)}>OK</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AlertNotifier;
